/**
 * ActaRepo sobre Supabase. Misma lógica que DexieActaRepo.
 * generarDesdeRds lee Tareas, RDs aprobados, SO y AFE (lectura cruzada).
 */

import type { ActaRepo } from "../../repos/ActaRepo";
import type { Acta, ActaFirma, Tarea, Rd, So, Afe } from "@app-types/entities";
import type { EstadoActa } from "@constants/estados";
import type { Role } from "@constants/roles";
import {
  puedeTransicionarActa,
  construirBorradorActa,
  codigoActa,
} from "@/features/actas/actasRules";
import { congelarMonto } from "@/lib/moneda";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";
import { ValidationError } from "../../repos/BaseRepo";

export class SupabaseActaRepo extends SupabaseBaseRepo<Acta> implements ActaRepo {
  constructor(tenantId: string) {
    super("acta", tenantId);
  }

  async list(filter?: Partial<Acta>): Promise<Acta[]> {
    const all = await this.listAll();
    if (filter?.estado) return all.filter((a) => a.estado === filter.estado);
    if (filter?.soId) return all.filter((a) => a.soId === filter.soId);
    return all.sort((a, b) => b.periodoFin.localeCompare(a.periodoFin));
  }

  async listBySo(soId: string): Promise<Acta[]> {
    return (await this.listAll())
      .filter((a) => a.soId === soId)
      .sort((a, b) => b.periodoFin.localeCompare(a.periodoFin));
  }

  async listByEstado(estado: EstadoActa): Promise<Acta[]> {
    return (await this.listAll()).filter((a) => a.estado === estado);
  }

  async generarDesdeRds(soId: string, fechaCorte: string | null, userEmail: string): Promise<string> {
    const [tareasAll, rdsAll, sosAll, afesAll] = await Promise.all([
      this.listOtherEntity<Tarea>("tarea"),
      this.listOtherEntity<Rd>("rd"),
      this.listOtherEntity<So>("so"),
      this.listOtherEntity<Afe>("afe"),
    ]);
    const tareas = tareasAll.filter((t) => t.soId === soId);
    const rdsDeLaOt = rdsAll.filter((r) => r.soId === soId);
    const actasDeLaOt = await this.listBySo(soId);
    const so = sosAll.find((s) => s.id === soId);

    // Ver DexieActaRepo: todo el calculo vive en actasRules.
    const borrador = construirBorradorActa(so, tareas, rdsDeLaOt, actasDeLaOt, fechaCorte);
    if (!borrador.ok) throw new ValidationError(borrador.mensaje);
    const { periodoInicio, periodoFin, lineas, total, ultimoRdIncluido } = borrador;
    const moneda: "USD" | "COP" = so?.moneda ?? "USD";
    const afe = so?.afeId ? afesAll.find((a) => a.id === so.afeId) : null;
    const trmAfe = afe?.trm ?? so?.trmCongelada ?? 0;
    const { montoUsd: totalUsd, montoCop: totalCop, trmCongelada } = congelarMonto(total, moneda, trmAfe);
    const codigo = codigoActa(so?.codigo ?? soId, periodoFin);

    const now = new Date().toISOString();
    const nueva: Acta = {
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      codigo,
      soId,
      periodoInicio,
      periodoFin,
      estado: "BORRADOR",
      lineas,
      total,
      moneda,
      totalUsd,
      totalCop,
      trmCongelada,
      // Frontera real de lo certificado — ver Acta.ultimoRdIncluido.
      ultimoRdIncluido,
      firmas: [],
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
    };
    await this.createRaw(nueva);
    return nueva.id;
  }

  async get(id: string): Promise<Acta | null> {
    return this.getById(id);
  }

  async create(data: Omit<Acta, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nueva: Acta = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nueva);
    return nueva.id;
  }

  async update(id: string, data: Partial<Acta>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Acta ${id} not found`);
    const updated: Acta = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: EstadoActa, _userEmail: string, _observaciones?: string): Promise<void> {
    const acta = await this.get(id);
    if (!acta) throw new Error(`Acta ${id} not found`);
    if (!puedeTransicionarActa(acta.estado, nextState)) {
      throw new Error(`Transicion ${acta.estado} -> ${nextState} no permitida`);
    }
    await this.update(id, { estado: nextState }, acta.version);
  }

  async firmarPorRol(
    id: string,
    rol: Role,
    userEmail: string,
    decision: "APROBADA" | "RECHAZADA",
    observaciones?: string,
  ): Promise<void> {
    const acta = await this.get(id);
    if (!acta) throw new Error(`Acta ${id} not found`);
    const firma: ActaFirma = {
      rol,
      usuario: userEmail,
      fecha: new Date().toISOString(),
      decision,
      ...(observaciones !== undefined && { observaciones }),
    };
    const nuevasFirmas = [...acta.firmas.filter((f) => f.rol !== rol), firma];
    let nextState: EstadoActa = acta.estado;
    if (decision === "RECHAZADA") {
      nextState = "RECHAZADA";
    } else if ((rol === "ADOR" || rol === "PLANNER") && acta.estado === "BORRADOR") {
      nextState = "EN_REVISION_INT";
    } else if (rol === "INT" && acta.estado === "EN_REVISION_INT") {
      nextState = "APROBADA_INT";
    } else if (rol === "PL2" && (acta.estado === "APROBADA_INT" || acta.estado === "EN_REVISION_PL2")) {
      nextState = "APROBADA";
    }
    const updates: Partial<Acta> = { firmas: nuevasFirmas };
    if (nextState !== acta.estado) updates.estado = nextState;
    await this.update(id, updates, acta.version);
  }
}
